import { useMemo } from 'react';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { TranslationKey } from '~/lib';
import { SalesforceProduct } from '~/lib/salesforce/types';
import { useTranslation } from '~/shared/utils/translation';
import { TranslateFn } from '~/shared/utils/translation/hooks/useTranslation';
import { usePage } from '~/templates/pages';

interface ProductSpecs {
    name?: string;
    text?: string;
    url?: string;
    target?: string;
    id?: string;
    itemProp?: string;
}

interface SummarySpecificationProps {
    specifications?: ProductSpecs[];
}

/**
 * List of spec names from the product from salesforce
 * we would like to use in the summary.
 * We need to add an Umbraco translation key for each.
 */
const specAttributes = ['c_color', 'c_material'];

const customerSpecAttributes = [
    'c_ItemTestWinnerDescription',
    'ean',
    'id',
    'unit',
    'c_ProductDesigner',
    'c_modelNumber',
];

const storePersonnelSpecAttributes = [
    'c_ItemTestWinnerDescription',
    'ean',
    'id',
    'c_productStatusCode',
    'unit',
    'c_ProductDesigner',
    'c_modelNumber',
    'c_SupplierName',
    'c_SupplierNo',
];

enum SpecificationItemProp {
    brand = 'brand', // The brand or producer of the product.
    c_color = 'color', // The color of the product.
    c_dimDepth = 'depth', // The depth or height of the product.
    c_dimDiameter = 'diameter', // The diameter of the product.
    c_dimHeight = 'height', // The height or depth of the product.
    c_dimLength = 'length', // The length of the product.
    c_dimWeight = 'weight', // The weight of the product.
    c_dimWidth = 'width', // The width of the product.
    c_material = 'material', // The material of the product.
    c_modelNumber = 'modelNumber', // The model number of the product.
    c_ProductDesigner = 'productDesigner', // The designer of the product.
    c_ItemFreeTextDimension = 'ItemFreeTextDimension',
    availability = 'availability', // The availability status of the product.
    category = 'category', // The category of the product.
    ean = 'gtin13', // The GTIN-13 code of the product.
    id = 'mpn', // The manufacturer part number of the product.
    isPartOf = 'series', // The product series or group.
    series = 'series', // The product series or group.
    subCategory = 'subcategory', // The subcategory of the product.
    unit = 'quantity', // The quantity of the product.
}

const parseSpecifications = (specifications: string | undefined, translate: TranslateFn) => {
    try {
        if (!specifications) return [];

        const parsed = JSON.parse(specifications);

        if (!parsed) return [];

        //NOTE: Units Bunddiameter, Kapacitetsenhed and Kapacitet sætUnit
        //are merged with their respective values. They should not be shown individually.

        if (parsed?.Bunddiameter && parsed?.BunddiameterUnit)
            parsed.Bunddiameter += ` ${parsed.BunddiameterUnit}`;
        delete parsed.BunddiameterUnit;

        if (parsed?.Kapacitet && parsed?.KapacitetUnit) {
            parsed.Kapacitet += ` ${parsed.KapacitetUnit}`;
        } else if (parsed?.Kapacitet && parsed?.Kapacitetsenhed) {
            parsed.Kapacitet += ` ${parsed.Kapacitetsenhed}`;
        }

        delete parsed.KapacitetUnit;
        delete parsed.Kapacitetsenhed;

        if (parsed?.['Kapacitet sæt'] && parsed?.['Kapacitet sætUnit'])
            parsed['Kapacitet sæt'] += ` ${parsed['Kapacitet sætUnit']}`;
        delete parsed['Kapacitet sætUnit'];

        if (parsed?.['Fyldvægt'] && parsed?.['FyldvægtUnit'])
            parsed['Fyldvægt'] += ` ${parsed['FyldvægtUnit']}`;
        delete parsed['FyldvægtUnit'];

        // Hide complementary products from specifications display
        // They should remain in data flow for Related Products functionality
        delete parsed['Komplementær varianter'];

        const result = Object.entries(parsed).map(([name, text]) => ({
            name,
            text: Array.isArray(text)
                ? text.join(', ')
                : typeof text === 'boolean' ||
                    text === 'True' ||
                    text === 'False' ||
                    text === 'true' ||
                    text === 'false'
                  ? translate(`pdp.spec.${String(text).toLocaleLowerCase()}` as TranslationKey)
                  : text,
        }));

        if (result) {
            return result as ProductSpecs[];
        }
        return [];
    } catch (e) {
        console.debug(e);
        return [];
    }
};

export const useProductDisplaySpecifications = (
    product: SalesforceProduct,
): SummarySpecificationProps['specifications'] | null => {
    const { translate } = useTranslation();
    const { isStorePersonnel } = useCustomer();
    const { productCategoryLinks } = usePage();

    const formattedSpecifications = useMemo(() => {
        if (!product) return null;

        /* Links to other PLP pages, we get this from Umbraco on the page itself */
        const [brand, serie, ...res] = Object.entries(productCategoryLinks ?? {}).map(
            ([key, value = {}]) => ({
                ...value,
                name: translate(`pdp.spec.${key}` as TranslationKey),
                itemProp:
                    SpecificationItemProp[key as keyof typeof SpecificationItemProp] || undefined,
            }),
        );

        const additionalSpecs = isStorePersonnel
            ? storePersonnelSpecAttributes
            : customerSpecAttributes;

        /* See what are available on the product */
        const specsAvailable = Object.keys(product ?? {}).filter((key) =>
            specAttributes.includes(key),
        );
        const specsAvailable2 = additionalSpecs.filter((key) => product[key]);

        /* Map the product to the spec names */
        const specs = specsAvailable.map((key) => ({
            name: translate(`pdp.spec.${key}` as TranslationKey),
            text: product[key]?.replace(/,/g, ', '),
            itemProp: SpecificationItemProp[key as keyof typeof SpecificationItemProp] || undefined,
        }));

        const specs2 = specsAvailable2?.map((key) => ({
            name: translate(`pdp.spec.${key}` as TranslationKey),
            text: product[key]?.replace(/,/g, ', '),
            itemProp: SpecificationItemProp[key as keyof typeof SpecificationItemProp] || undefined,
        }));

        /* Get the rest of the specs from the product */
        const restSpecs = parseSpecifications(product.c_specifications, translate);

        if (product.c_dimWeight && product.c_dimWeightUnit) {
            restSpecs.push({
                name: translate('pdp.spec.c_dimWeight'),
                text: `${product.c_dimWeight} ${product.c_dimWeightUnit}`,
                itemProp:
                    SpecificationItemProp['c_dimWeight' as keyof typeof SpecificationItemProp] ||
                    undefined,
            });
        }

        if (product.c_ItemFreeTextDimension) {
            restSpecs.push({
                name: translate('pdp.spec.c_ItemFreeTextDimension'),
                text: product.c_ItemFreeTextDimension,
                itemProp: SpecificationItemProp['c_ItemFreeTextDimension'],
            });
        }

        ['c_dimHeight', 'c_dimWidth', 'c_dimLength', 'c_dimDepth', 'c_dimDiameter'].forEach((x) => {
            const result = product[x];
            if (result && product.c_dimMessurementUnit) {
                restSpecs.push({
                    name: translate(`pdp.spec.${x}` as TranslationKey),
                    text: `${result} ${product.c_dimMessurementUnit}`,
                    itemProp: SpecificationItemProp['unit'] || undefined,
                });
            }
        });

        return [brand, serie, ...specs, ...restSpecs, ...res, ...specs2].filter((x) => x.text);
    }, [product, isStorePersonnel]);

    return formattedSpecifications;
};
